import { FetchSecretKeyResponseDto } from "@/data/dto/fetch-secret-key-reponse-dto";
import { OkResponseDto } from "@/data/dto/ok-response-dto";
import { FetchSecretKeyByCookieRequestPayload } from "@/data/payload/auth/fetch-secret-key-by-cookie-payload";
import { URLList } from "@/data/url_list";
import { clearPasswordChangeCookies, clearUserRoleCookie } from "@/utils/cookie-utils";
import { CryptoUtil } from "@/utils/crypto/crypto";
import { AES_KEY, TOKEN_STORAGE_KEY } from "@/utils/global-constants";
import { createLogger, withApiLogging } from "@/utils/logger/logger";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATErrorModel, SATResult } from "@sadtx/sat-error";
import { SecureStorageService } from "@sadtx/secure-storage";

export class AuthSource {
  private readonly logger = createLogger("AuthSource");

  constructor(
    private readonly client: FetchApiClientMethod,
    private readonly cryptoUtil: CryptoUtil,
    private readonly secureStorage: SecureStorageService,
  ) {}

  async exchangeSecretKeyByCookie(): Promise<SATResult<void, SATError>> {
    try {
      const keyPair = await this.cryptoUtil.webGenerateRSAKeyPair();
      const pemPublicKey = await this.cryptoUtil.exportKeyToPem(keyPair.publicKey, "public");
      const payload: FetchSecretKeyByCookieRequestPayload = {
        input: pemPublicKey,
      };
      const encryptKeyResponseData = await this.fetchSecretKeyWithCookie(payload);

      if (encryptKeyResponseData.isFailure) {
        return SATResult.failure(encryptKeyResponseData.error);
      }

      const aesKey = await this.cryptoUtil.webDecryptRSA(
        keyPair.privateKey,
        encryptKeyResponseData.value.output,
      );
      await this.secureStorage.setItem(sessionStorage, AES_KEY, this.extractKeyValue(aesKey));
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async reissueWebUserToken(): Promise<SATResult<OkResponseDto, SATError>> {
    const url = URLList.USER.RESET_TOKEN;
    try {
      const data = await withApiLogging(this.logger, { method: "POST", url }, async () => {
        const response = await this.client.post(url);
        return response.data as OkResponseDto;
      });
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async clearAuthStorage(): Promise<SATResult<void, SATError>> {
    try {
      await this.secureStorage.removeItem(sessionStorage, AES_KEY);
      if (typeof localStorage !== "undefined") {
        await this.secureStorage.removeItem(localStorage, TOKEN_STORAGE_KEY);
      }
      clearUserRoleCookie();
      clearPasswordChangeCookies();
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  private extractKeyValue(aesKey: string | object | null | undefined): string {
    if (!aesKey) {
      throw new SATErrorModel.FailToGetSecretKey("AES key is empty");
    }

    if (typeof aesKey === "string") {
      try {
        const parsed = JSON.parse(aesKey) as { key?: unknown };
        return typeof parsed.key === "string" ? parsed.key : aesKey;
      } catch {
        return aesKey;
      }
    }

    if ("key" in aesKey && typeof aesKey.key === "string") {
      return aesKey.key;
    }

    return String(aesKey);
  }

  private async fetchSecretKeyWithCookie(
    payload: FetchSecretKeyByCookieRequestPayload,
  ): Promise<SATResult<FetchSecretKeyResponseDto, SATError>> {
    const url = URLList.AUTH.GET_SECRET_KEY_BY_COOKIE;
    try {
      const data = await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data as FetchSecretKeyResponseDto;
      });
      if (!data?.output) {
        throw new SATErrorModel.FailToGetSecretKey(
          "Failed to receive valid encryption key from server",
        );
      }
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }
}
